import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import whiteLogo from "@/assets/logos/internal/WaytoraWhiteTextLogo.svg";
import api from "@/config/axiosConfig";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Background photo component
const BackgroundPhoto = ({
  top,
  left,
  width,
  height,
  delay,
  src,
}: {
  top: number;
  left: number;
  width: string;
  height: string;
  delay: number;
  src: string;
}) => (
  <div
    className="absolute rounded-xl overflow-hidden"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width,
      height: height,
      animation: `float 20s ease-in-out ${delay}s infinite`,
      zIndex: 1,
    }}
  >
    <img
      src={src}
      alt=""
      loading="eager"
      className="w-full h-full object-cover opacity-70 blur-xs"
      style={{ transform: "scale(1.1)" }}
    />
  </div>
);

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayPhotos, setDisplayPhotos] = useState<Array<any>>([]);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Background photos for the contact page
  const photos = [
    {
      width: "450px",
      height: "350px",
      top: 15,
      left: 10,
      delay: 0,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG1.jpg",
    },
    {
      width: "400px",
      height: "300px",
      top: 15,
      left: 60,
      delay: 1,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG2.jpg",
    },
    {
      width: "420px",
      height: "320px",
      top: 60,
      left: 10,
      delay: 2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG3.jpg",
    },
    {
      width: "430px",
      height: "330px",
      top: 60,
      left: 60,
      delay: 2.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG5.jpg",
    },
  ];

  // Listen for window resize and update state
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Initial setup
    updatePhotos();

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update photos when window width changes
  useEffect(() => {
    updatePhotos();
  }, [windowWidth]);

  // Responsive photo config
  const updatePhotos = () => {
    // Adjust photos for smaller screens
    if (windowWidth < 640) {
      // For very small screens, only show one photo
      setDisplayPhotos(photos.slice(0, 1));
    } else if (windowWidth < 1024) {
      // For medium screens, show two photos
      setDisplayPhotos(photos.slice(0, 2));
    } else {
      // For large screens, show all photos
      setDisplayPhotos(photos);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.content.post("/contact", formData);

      toast({
        title: "Message sent successfully",
        description: "We will get back to you as soon as possible.",
      });

      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast({
        title: "Error sending message",
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative w-full min-h-screen flex flex-col bg-black text-white overflow-hidden">
      {/* Animation style */}
      <style>
        {`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-10px, 10px) rotate(1deg); }
          50% { transform: translate(0, 20px) rotate(0deg); }
          75% { transform: translate(10px, 10px) rotate(-1deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        `}
      </style>

      {/* Background Photos Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {displayPhotos.map((photo, index) => (
          <BackgroundPhoto
            key={index}
            width={windowWidth < 640 ? "100%" : photo.width}
            height={windowWidth < 640 ? "100%" : photo.height}
            top={windowWidth < 640 ? 0 : photo.top}
            left={windowWidth < 640 ? 0 : photo.left}
            delay={photo.delay}
            src={photo.src}
          />
        ))}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[2]"></div>
      </div>

      {/* Header */}
      <Header showHomeButton={true} />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-[320px] xs:max-w-sm sm:max-w-md p-6 sm:p-6 md:p-8 rounded-xl border border-white/20 bg-black/40 backdrop-blur-md shadow-2xl z-10 animate-fadeIn">
          <h1 className="text-xl xs:text-xl sm:text-2xl font-light mb-6 sm:mb-8 text-center text-white/90">
            Reach Out
          </h1>

          <p className="text-sm xs:text-sm text-gray-300 mb-5 text-center">
            For US-specific inquiries, please{" "}
            <a
              onClick={() => navigate("/contact/us")}
              className="text-indigo-400 hover:text-indigo-300 underline cursor-pointer"
            >
              visit our US contact page
            </a>
            .
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm xs:text-sm text-gray-300 mb-2 sm:mb-2 font-light"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 xs:px-3 sm:px-4 py-3 sm:py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm xs:text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 
                transition-all duration-300"
                placeholder="Your name"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm xs:text-sm text-gray-300 mb-2 sm:mb-2 font-light"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 xs:px-3 sm:px-4 py-3 sm:py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm xs:text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500
                transition-all duration-300"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm xs:text-sm text-gray-300 mb-2 sm:mb-2 font-light"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={windowWidth < 400 ? 4 : 4}
                className="w-full px-4 xs:px-3 sm:px-4 py-3 sm:py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm xs:text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none
                transition-all duration-300"
                placeholder="How can we help?"
                required
              />
            </div>

            <div className="flex justify-center pt-3 sm:pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 xs:px-6 sm:px-8 py-2.5 xs:py-2 sm:py-3 text-sm sm:text-sm text-white border border-white/30 rounded-lg
                hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-300 relative group backdrop-blur-sm w-full sm:w-auto"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-4 h-4 sm:w-4 sm:h-4 mr-2 animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  <span className="relative z-10">Send Message</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactPage;
